/**
 * Shared employee badge list mapping and query building (Your Badges tab, dashboard).
 */

import { isAboutToExpireExpirationFilter } from "@/utils/badgeExpiration";
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";

const BADGE_DOT_BY_STATUS = {
  given: "Given",
  obsolete: "Obsolete",
};

const DEFAULT_BADGE_DOT = "Good Standing";

// Output keys of the dashboard counts / table counters are kept as-is (consumed by the
// dashboards and YourBadgesTable); only the incoming level strings go through the adapter.
const DASHBOARD_BADGE_LEVELS = ["apprentice", "professional", "coach", "master"];

const LEVEL_COUNT_OUTPUT_KEYS = ["Apprentice", "Professional", "Coach", "Master"];

const FILTER_CURRENT = "Current";
const FILTER_REMOVED = "Removed";
const API_STATUS_CURRENT = "current";
const API_STATUS_REMOVED = "removed";

/** Backend placeholder value for an unset remove_reason field. */
const API_PLACEHOLDER_REMOVE_REASON = "string";

export function getBadgeDotFromStatus(status) {
  const normalized = String(status ?? "").toLowerCase();
  return BADGE_DOT_BY_STATUS[normalized] ?? DEFAULT_BADGE_DOT;
}

/** Read a level's count from API `level_counts`, accepting legacy and new level keys in any case. */
function readLevelCount(levelCounts, outputKey) {
  const levelKey = normalizeBadgeLevel(outputKey);
  const matchingKeys = Object.keys(levelCounts).filter(
    (key) => normalizeBadgeLevel(key) === levelKey
  );
  const value = matchingKeys.map((key) => levelCounts[key]).find((count) => count != null);
  return Number(value) || 0;
}

/** API `level_counts` → table counter shape. */
export function normalizeBadgeLevelCounts(levelCounts) {
  if (!levelCounts || typeof levelCounts !== "object") {
    return null;
  }
  return LEVEL_COUNT_OUTPUT_KEYS.reduce((counts, outputKey) => {
    counts[outputKey] = readLevelCount(levelCounts, outputKey);
    return counts;
  }, {});
}

/** Dashboard card counts from current badge API rows. */
export function deriveDashboardBadgeCountsFromResults(results) {
  const counts = Object.fromEntries(DASHBOARD_BADGE_LEVELS.map((level) => [level, 0]));

  for (const item of results ?? []) {
    if (!item?.is_current) {
      continue;
    }
    const levelKey = DASHBOARD_BADGE_LEVELS.find(
      (level) => normalizeBadgeLevel(level) === normalizeBadgeLevel(item.level)
    );
    if (levelKey) {
      counts[levelKey] += 1;
    }
  }

  return counts;
}

function mapStampFromActor(actor, dateValue) {
  if (!actor) {
    return null;
  }
  return {
    name: actor.name,
    picture: actor.picture || "",
    userType: "user",
    date: dateValue || "",
  };
}

function mapCurrentBadge(item) {
  return {
    level: item.level || "",
    dot: getBadgeDotFromStatus(item.status),
    view: item.cause,
    expiration_date: item.expiration_date,
    has_expiration_if_obsolete: item.expires_when_obsolete,
    is_current: item.is_current,
  };
}

function mapResponsibility(item) {
  return {
    id: item.issued_for?.id,
    code: item.issued_for?.code,
    name: item.issued_for?.name,
    status: item.issued_to?.current_status,
  };
}

function normalizeRemoveReason(removeReason) {
  return removeReason === API_PLACEHOLDER_REMOVE_REASON ? "" : removeReason;
}

function mapEmployeeBadgeRow(item) {
  return {
    id: item.id,
    is_current: item.is_current,
    issued_to: item.issued_to,
    issued_by: item.issued_by,
    issued_on: item.issued_on,
    access: item.access,
    removed_on: item.removed_on,
    currentBadge: mapCurrentBadge(item),
    responsibility: mapResponsibility(item),
    removedBy: mapStampFromActor(item.removed_by, item.removed_on),
    grantedBy: mapStampFromActor(item.awarded_by, item.awarded_on),
    reason: normalizeRemoveReason(item.remove_reason),
    status: item.status,
    cause: item.cause,
    responsibility_status: item.responsibility_status,
  };
}

/** Map proficiency employee badge API rows → YourBadgesTable rows. */
export function mapEmployeeBadgeApiResultsToTableRows(results) {
  return (results ?? []).map(mapEmployeeBadgeRow);
}

function hasStandingFilters(badgeStandingFilters) {
  return Array.isArray(badgeStandingFilters) && badgeStandingFilters.length > 0;
}

/** True when list needs client-side standing and/or expiration filtering. */
export function needsClientSideBadgeListFiltering(listQuery) {
  if (isAboutToExpireExpirationFilter(listQuery?.expirationFilter)) {
    return true;
  }
  return hasStandingFilters(listQuery?.badgeStandingFilters);
}

function resolveBadgeListStatus(appliedFilters = []) {
  const hasCurrent = appliedFilters.includes(FILTER_CURRENT);
  const hasRemoved = appliedFilters.includes(FILTER_REMOVED);

  if (hasCurrent && !hasRemoved) {
    return API_STATUS_CURRENT;
  }
  if (!hasCurrent && hasRemoved) {
    return API_STATUS_REMOVED;
  }
  return undefined;
}

/**
 * Build getEmployeeBadges query from list UI state.
 * @param {{ page: number, perPage: number, listQuery: { search: string, appliedFilters: string[], selectedLevels: string[], badgeStandingFilters?: string[] } }} opts
 */
export function buildEmployeeBadgesListQuery({ page, perPage, listQuery }) {
  const limit = perPage;
  const offset = (page - 1) * limit;
  const query = { limit, offset };

  const status = resolveBadgeListStatus(listQuery?.appliedFilters);
  if (status) {
    query.status = status;
  }

  const searchText = listQuery?.search?.trim();
  if (searchText) {
    query.search = searchText;
  }

  if (listQuery?.selectedLevels?.length) {
    query.level = [...listQuery.selectedLevels];
  }

  return query;
}
